import type { Idl } from '@coral-xyz/anchor';
import { AnchorProvider, Program } from '@coral-xyz/anchor';
import { type Connection, PublicKey } from '@solana/web3.js';
import { CP_SWAP_PROGRAM_ID, MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';

/**
 * The IDLs live in `idl/` inside this package — vendored copies of
 * `contracts/markets/idl/markets.json` and
 * `contracts/morfi-cp-swap/idl/raydium_cp_swap.json`. The `contracts/` git
 * submodule is private, so builds that clone without submodules (Vercel)
 * resolve these instead. `scripts/check-idl-vendored.mjs` fails precommit when
 * a copy diverges from the submodule.
 */

/** Cached IDL JSON to avoid repeated dynamic imports. */
let cachedIdl: Idl | null = null;

/** Cached cp-swap IDL JSON to avoid repeated dynamic imports. */
let cachedCpSwapIdl: Idl | null = null;

/** Cached Program instances by programId string. */
const programCache: Map<string, Program> = new Map<string, Program>();

/**
 * Load the IDL JSON lazily via dynamic import.
 * Cached after first load.
 */
async function loadIdl(): Promise<Idl> {
  if (cachedIdl) {
    return cachedIdl;
  }
  // Dynamic import — keeps IDL out of the static bundle
  const mod = await import('../../idl/markets.json', {
    with: { type: 'json' },
  });
  cachedIdl = mod.default as Idl;
  return cachedIdl;
}

/** Load the cp-swap (Raydium CPMM fork) IDL lazily. Cached after first load. */
async function loadCpSwapIdl(): Promise<Idl> {
  if (cachedCpSwapIdl) {
    return cachedCpSwapIdl;
  }
  const mod = await import('../../idl/raydium_cp_swap.json', {
    with: { type: 'json' },
  });
  cachedCpSwapIdl = mod.default as Idl;
  return cachedCpSwapIdl;
}

/**
 * Get an Anchor Program instance for the morfi-opinion program.
 * Lazily loads the IDL and caches the Program by programId.
 *
 * Uses a read-only provider (no wallet) since the SDK only builds transactions.
 */
export async function getProgram(
  connection: Connection,
  programId: PublicKey = MORFI_OPINION_PROGRAM_ID,
): Promise<Program> {
  const key = programId.toBase58();
  const cached = programCache.get(key);
  if (cached) {
    return cached;
  }

  const idl = await loadIdl();
  // Read-only provider — no wallet needed for building transactions
  const provider = new AnchorProvider(connection, {
    publicKey: PublicKey.default,
    signAllTransactions: async <T>(txs: T[]): Promise<T[]> => txs,
    signTransaction: async <T>(tx: T): Promise<T> => tx,
  });
  const program = new Program({ ...idl, address: programId.toBase58() }, provider);
  programCache.set(key, program);
  return program;
}

/**
 * Get an Anchor Program instance for the cp-swap (Raydium CPMM fork) program.
 * Lazily loads the cp-swap IDL and caches the Program by programId. Used to
 * build `swap_base_input` instructions for graduated-market trading.
 *
 * Uses a read-only provider (no wallet) since the SDK only builds transactions.
 */
export async function getCpSwapProgram(
  connection: Connection,
  programId: PublicKey = CP_SWAP_PROGRAM_ID,
): Promise<Program> {
  const key = programId.toBase58();
  const cached = programCache.get(key);
  if (cached) {
    return cached;
  }

  const idl = await loadCpSwapIdl();
  const provider = new AnchorProvider(connection, {
    publicKey: PublicKey.default,
    signAllTransactions: async <T>(txs: T[]): Promise<T[]> => txs,
    signTransaction: async <T>(tx: T): Promise<T> => tx,
  });
  const program = new Program({ ...idl, address: programId.toBase58() }, provider);
  programCache.set(key, program);
  return program;
}

/** Clear the IDL and program cache (for testing). */
export function clearProgramCache(): void {
  cachedIdl = null;
  cachedCpSwapIdl = null;
  programCache.clear();
}
